import React from 'react';

/* ─── Shared calendar helpers ──────────────────────────────────────
   Used by Events.js, EventDetail.js, RaceMatrix.js
   ------------------------------------------------------------------ */

/**
 * Checks whether a driver's canonical category (e.g. "Elite", "Amateure")
 * corresponds to an event schedule category name (e.g. "Elite-Amateure A").
 *
 * Rules:
 *  - Gender is enforced first: male drivers never match female classes and vice versa.
 *  - Then prefix matching: "Elite" matches anything starting with "elite" (so
 *    "Elite", "Elite-Amateure", "Elite/Amateur" all match).
 *  - "Amateure" matches "amateur*", "ct und …", "ct-…"  but NOT "Elite-Amateure"
 *    (because "elite-amateure" doesn't start with "amateur").
 */
export function driverCatMatchesScheduleCat(driverCat, scheduleCatName) {
  if (!scheduleCatName) return true;   // no filter → match everything
  if (!driverCat)       return false;

  const sc = scheduleCatName.toLowerCase();
  const dc = driverCat.toLowerCase();

  if (sc === dc) return true;

  // Gender guard
  const isFem = s => /frauen|damen|innen/.test(s);
  if (isFem(sc) !== isFem(dc)) return false;

  // Prefix rules: canonical driver category → prefixes a schedule name can start with
  const rules = {
    // Männer
    'elite':    [['elite']],
    'u23':      [['u23']],
    'amateure': [['amateur'], ['ct und'], ['ct-']],
    'u19':      [['junior'], ['u19']],
    'u17':      [['jugend'], ['u17'], ['junior']],
    'u15':      [['u15']],
    'u13':      [['u13']],
    'u11':      [['u11']],
    'master 1': [['masters 1'], ['master 1']],
    'master 2': [['masters 2'], ['master 2']],
    'master 3': [['masters 3'], ['master 3']],
    'master 4': [['masters 4'], ['master 4']],
    // Frauen (gender already enforced above)
    'elite frauen':      [['elite'], ['frauen'], ['damen']],
    'u23 frauen':        [['u23']],
    'u19 juniorinnen':   [['junior'], ['u19']],
    'u17 juniorinnen':   [['jugend'], ['u17'], ['junior']],
    'u15 juniorinnen':   [['u15']],
    'masters frauen':    [['master'], ['seniorinnen']],
  };

  const prefixes = rules[dc];
  if (!prefixes) return false;
  // startsWith only — sc.includes(p) would cause "elite-amateur".includes("amateur")
  // to wrongly match Amateure drivers when filtering by Elite-Amateur
  return prefixes.some(([p]) => sc.startsWith(p));
}

/** Build shared date/time data used by both export functions */
export function buildCalendarData(event, driverCategory) {
  const pad = (n) => String(n).padStart(2, '0');
  const d = new Date(event.date);
  const dateStr = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  const cats = event.categories || [];
  const ownCat = driverCategory && cats.find(c => driverCatMatchesScheduleCat(driverCategory, c.category) && c.start_time);
  const firstCat = cats.find(c => c.start_time);
  const startTime = (ownCat || firstCat)?.start_time || null;

  let dtstart, dtend, googleStart, googleEnd;
  if (startTime) {
    const [h, m] = startTime.split(':');
    const endH = Math.min(parseInt(h) + 4, 23);
    dtstart     = `${dateStr}T${pad(parseInt(h))}${pad(parseInt(m))}00`;
    dtend       = `${dateStr}T${pad(endH)}${pad(parseInt(m))}00`;
    googleStart = `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}T${pad(parseInt(h))}${pad(parseInt(m))}00`;
    googleEnd   = `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}T${pad(endH)}${pad(parseInt(m))}00`;
  } else {
    dtstart = dtend = dateStr;
    googleStart = googleEnd = `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}`;
  }

  const desc = [
    event.description || '',
    event.track_type ? `Streckentyp: ${event.track_type}` : '',
    event.track_length ? `Streckenlänge: ${event.track_length} m` : '',
    cats.map(c => `${c.category}${c.start_time ? ` · Start: ${c.start_time}` : ''}`).join(' | '),
    event.url ? `Ausschreibung: ${event.url}` : '',
  ].filter(Boolean).join('\\n');

  return { dateStr, dtstart, dtend, startTime, desc, cats, googleStart, googleEnd };
}

/** Open event in Apple Calendar / system calendar via ICS blob */
export function openInAppleCalendar(event, driverCategory) {
  const pad = (n) => String(n).padStart(2, '0');
  const { dtstart, dtend, startTime, desc } = buildCalendarData(event, driverCategory);
  const uid = `sturmvogel-event-${event.id}@racing`;
  const now = new Date();
  const stamp = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}T${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}Z`;

  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Sturmvogel Racing//DE',
    'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', 'BEGIN:VEVENT',
    `UID:${uid}`, `DTSTAMP:${stamp}`,
    startTime ? `DTSTART:${dtstart}` : `DTSTART;VALUE=DATE:${dtstart}`,
    startTime ? `DTEND:${dtend}`     : `DTEND;VALUE=DATE:${dtend}`,
    `SUMMARY:${event.name}`,
    event.location ? `LOCATION:${event.location}` : '',
    desc ? `DESCRIPTION:${desc}` : '',
    event.url ? `URL:${event.url}` : '',
    'END:VEVENT', 'END:VCALENDAR',
  ].filter(Boolean).join('\r\n');

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', `${event.name.replace(/[^a-z0-9]/gi, '_')}.ics`);
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Open event directly in Google Calendar in a new tab */
export function openInGoogleCalendar(event, driverCategory) {
  const { googleStart, googleEnd, desc } = buildCalendarData(event, driverCategory);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.name,
    dates: `${googleStart}/${googleEnd}`,
    details: desc.replace(/\\n/g, '\n'),
    ...(event.location ? { location: event.location } : {}),
  });
  window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank');
}

/* ─── CalendarPrompt dialog ──────────────────────────────────────── */
export function CalendarPrompt({ ev, driverCategory, onDismiss }) {
  const cats = ev.categories || [];
  const ownCat = driverCategory && cats.find(c => driverCatMatchesScheduleCat(driverCategory, c.category) && c.start_time);
  const firstCat = cats.find(c => c.start_time);
  const startTime = (ownCat || firstCat)?.start_time || null;
  const dateFormatted = new Date(ev.date).toLocaleDateString('de-DE', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });

  const btnBase = {
    width: '100%', padding: '13px', border: 'none',
    fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700,
    letterSpacing: '0.1em', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  };

  return (
    <div
      onClick={onDismiss}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface)', border: '1px solid rgba(var(--brand-primary-rgb),0.25)',
          padding: '28px 28px 24px', maxWidth: 380, width: '100%',
          boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          background: 'rgba(var(--brand-primary-rgb),0.12)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
        }}>
          <span style={{ fontSize: 22 }}>✓</span>
        </div>

        <div style={{
          fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800,
          fontStyle: 'italic', color: '#e2e2e8', textTransform: 'uppercase',
          marginBottom: 6, lineHeight: 1.1,
        }}>
          Du bist dabei!
        </div>

        <div style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
          color: 'var(--on-surface-variant)', marginBottom: 20, lineHeight: 1.5,
        }}>
          <span style={{ color: '#e2e2e8', fontWeight: 700 }}>{ev.name}</span><br />
          {dateFormatted}
          {startTime && (
            <><br />{driverCategory ? `${driverCategory} · ` : ''}Start:{' '}
              <span style={{ color: 'var(--brand-primary)', fontWeight: 700 }}>{startTime} Uhr</span>
            </>
          )}
          {ev.location && <><br />{ev.location}</>}
        </div>

        <div style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
          color: 'var(--on-surface-variant)', letterSpacing: '0.1em',
          textTransform: 'uppercase', marginBottom: 8,
        }}>
          In Kalender eintragen
        </div>

        {/* Google Calendar */}
        <button
          onClick={() => { openInGoogleCalendar(ev, driverCategory); onDismiss(); }}
          style={{ ...btnBase, background: '#fff', color: '#202124', marginBottom: 8 }}
        >
          <svg width="16" height="16" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
            <path fill="#4285F4" d="M24 9.5c3.7 0 6.6 1.6 8.6 2.9l6.4-6.4C34.8 2.5 29.8 0 24 0 14.6 0 6.5 5.5 2.5 13.5l7.5 5.8C12 13.3 17.5 9.5 24 9.5z"/>
            <path fill="#34A853" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.8 7.2l7.4 5.7c4.3-4 6.8-9.9 6.8-16.9z"/>
            <path fill="#FBBC05" d="M10 28.7c-.5-1.4-.8-2.9-.8-4.5s.3-3.1.8-4.5L2.5 14c-1.6 3.2-2.5 6.8-2.5 10.5s.9 7.3 2.5 10.5l7.5-6.3z"/>
            <path fill="#EA4335" d="M24 48c6 0 11-2 14.6-5.4l-7.4-5.7c-2 1.3-4.6 2.1-7.2 2.1-6.5 0-12-3.8-14-9.3L2.5 35.5C6.5 43.5 14.6 48 24 48z"/>
          </svg>
          Google Calendar
        </button>

        {/* Apple / System Calendar */}
        <button
          onClick={() => { openInAppleCalendar(ev, driverCategory); onDismiss(); }}
          style={{
            ...btnBase,
            background: 'rgba(255,255,255,0.06)', color: '#e2e2e8',
            border: '1px solid rgba(255,255,255,0.12)', marginBottom: 10,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 48 48" fill="none" style={{ flexShrink: 0 }}>
            <rect width="48" height="48" rx="10" fill="#fff"/>
            <rect x="4" y="12" width="40" height="32" rx="4" fill="#fff" stroke="#e0e0e0" strokeWidth="1"/>
            <rect x="4" y="12" width="40" height="10" rx="2" fill="#FA3A35"/>
            <rect x="4" y="18" width="40" height="4" fill="#FA3A35"/>
            <circle cx="14" cy="10" r="2.5" fill="#c0c0c0"/>
            <circle cx="34" cy="10" r="2.5" fill="#c0c0c0"/>
            <rect x="6" y="5" width="5" height="10" rx="2.5" fill="#c0c0c0"/>
            <rect x="37" y="5" width="5" height="10" rx="2.5" fill="#c0c0c0"/>
            <text x="24" y="38" textAnchor="middle" fontFamily="sans-serif" fontSize="13" fontWeight="bold" fill="#1a1a1a">
              {new Date(ev.date).getDate()}
            </text>
          </svg>
          Apple / System Calendar
        </button>

        <button
          onClick={onDismiss}
          style={{
            width: '100%', padding: '10px', background: 'transparent',
            border: '1px solid rgba(59,73,75,0.4)', color: 'var(--on-surface-variant)',
            fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700,
            letterSpacing: '0.1em', cursor: 'pointer',
          }}
        >
          NEIN DANKE
        </button>
      </div>
    </div>
  );
}
